import React, { Component } from 'react'
import { inject, observer } from 'mobx-react'
import { observable, action, makeObservable } from 'mobx'
import { notify } from 'libs/common/notify'

import { FILE_UPLOAD_ACCEPT_IMAGE_FILES, FILE_UPLOAD_ACCEPT_POST_EXTENSIONS } from 'constants/files.constants'
import { I18nContext } from '@elo-kit/components/i18n/i18n'
import { LoadingSpinner } from '@elo-kit/components/loading-spinner/LoadingSpinner'

import { getFileExtension } from '@elo-kit/utils/files.utils'

import ImageCrop from '../ImageCrop'
import FileUploadWidget from '../FileUploadWidget'
import EloModal from '../EloModal'

import './modals.scss'

@inject('coversStore')
@observer
class CropImageModal extends Component {
  static contextType = I18nContext

  @observable cropParams = {}
  @observable loadedImage = null
  @observable submitDisabled = false
  @observable isValidFormat = true

  constructor(props) {
    super(props)
    makeObservable(this)
  }

  componentDidMount() {
    const { coversStore } = this.props

    coversStore.toggleLoading(false)
  }

  @action setCropParams = (cropParams) => (this.cropParams = cropParams)

  @action setLoadedImage = (loadedImage) => (this.loadedImage = loadedImage)

  @action setSubmitDisabled = ({ height, width }) => (this.submitDisabled = height === 0 || width === 0)

  @action setValidFormat = (isValid) => (this.isValidFormat = isValid)

  showWarningMessage = (fileExtension) => {
    const I18n = this.context
    const isValidFormat = FILE_UPLOAD_ACCEPT_POST_EXTENSIONS.includes(fileExtension.toLowerCase())
    this.setValidFormat(isValidFormat)

    if (!isValidFormat) {
      notify('error', I18n.t('react.shared.community.notific.invalid_file_type'))
    }
  }

  handleImageSave = () => {
    const { image, onCrop } = this.props
    onCrop(this.cropParams, this.loadedImage || image)
  }

  uploadOwnImage = (files) => {
    const { coversStore, isCommunity } = this.props
    const { length, ...uploadedFiles } = files
    const file = Object.values(uploadedFiles)[0]

    coversStore.createItem({ file }).then(({ data }) => {
      this.setLoadedImage(data?.file?.cdnUrl || data?.file?.original || '')

      if (isCommunity) {
        this.showWarningMessage(getFileExtension(data?.file?.name))
      }
    })
  }

  addMediathekImage = (files) => {
    const { isCommunity } = this.props
    const file = Object.values(files)[0]

    if (file) {
      this.setLoadedImage(file?.file?.cdnUrl || file.url)

      if (isCommunity) {
        this.showWarningMessage(getFileExtension(file.file?.name))
      }
    }
  }

  uploadDropboxImage = (files) => {
    const { coversStore, isCommunity } = this.props
    const file = files[0]

    coversStore.createItem({ fileRemoteUrl: file.link }).then(({ data }) => {
      this.setLoadedImage(data?.url || '')

      if (isCommunity) {
        this.showWarningMessage(getFileExtension(data?.file?.name))
      }
    })
  }

  handleImageRemove = () => {
    this.setCropParams({})
    this.setLoadedImage(null)
    this.props.onRemove()
  }

  handleCropChange = (crop) => {
    this.setCropParams(crop)
    this.setSubmitDisabled(crop)
  }

  render() {
    const {
      coversStore,
      cropParams,
      image,
      loaderLabel,
      onRemove,
      open,
      title,
      toggleModal,
      onCancel,
      onClose,
      showFileUpload = true,
      submitButtonChild,
      acceptedFiles = FILE_UPLOAD_ACCEPT_IMAGE_FILES,
      dropboxExtensions,
    } = this.props
    const I18n = this.context

    const submitButtonDisabled =
      coversStore.loading || this.submitDisabled || !(image || this.loadedImage) || !this.isValidFormat

    return (
      <EloModal
        isOpen={open}
        className='elo-modal--crop-image'
        submit={this.handleImageSave}
        title={title}
        toggle={toggleModal || (() => {})}
        onCancel={onCancel}
        onClose={onClose}
        submitButtonChild={submitButtonChild}
        submitDisabled={submitButtonDisabled}
      >
        {coversStore.loading ? (
          <LoadingSpinner />
        ) : (
          (image || this.loadedImage) && (
            <ImageCrop
              image={this.loadedImage || image}
              onCrop={this.setCropParams}
              onRemove={onRemove && this.handleImageRemove}
              cropParams={cropParams}
              onCropChange={this.handleCropChange}
              isFullSelection={this.props.isFullSelection}
            />
          )
        )}

        {showFileUpload && (
          <FileUploadWidget
            formField
            id='image-loader'
            label={loaderLabel}
            acceptedFiles={acceptedFiles}
            dropboxExtensions={dropboxExtensions}
            fetchMediathekFiles={coversStore.fetchList}
            saveMediathekFiles={this.addMediathekImage}
            mediathekFiles={coversStore.list}
            mediathekModalTitle={I18n.t('react.shared.select_covers')}
            uploadFiles={this.uploadOwnImage}
            uploadDropboxFiles={this.uploadDropboxImage}
            allowMultiple={false}
            initStore={coversStore}
          />
        )}
      </EloModal>
    )
  }
}

CropImageModal.displayName = 'CropImageModal'

export default CropImageModal
